import { useCallback, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import { api } from "../../api";
import { longDate } from "../../lib/format";
import { useI18n, type Dict } from "../../lib/i18n";
import { errorCode, money, priceOf, usageFraction, usePlan, type BillingState } from "../../lib/plan";
import { checkoutResult } from "../../lib/route";
import { Section, StatusLine, useStatus } from "./common";
import { apiErrorText } from "../../lib/apiError";

/**
 * The owner's plan, usage and payments.
 *
 * Owner-only (see lib/tabs.ts `ownerOnly`, and the server's `requireOwnerRole`
 * on every billing route). The plans are flat — one monthly price per
 * workspace, no seats to buy — so this screen only ever picks a plan. Two
 * things it must never pretend: a change of plan is not instant, because
 * Stripe confirms it through a webhook that lands after the browser comes
 * back; and the effective plan can differ from the stored one while a payment
 * is late, so both are said out loud rather than showing "Starter" to a
 * workspace living under Free rules.
 */

/** The server's stable `code`s, in the owner's language. */
function billingMessage(t: Dict, err: unknown): string {
  switch (errorCode(err)) {
    case "subscription_exists":
      return t.billing.errSubscriptionExists;
    case "no_customer":
      return t.billing.errNoCustomer;
    case "no_subscription":
      return t.billing.errNoSubscription;
    case "billing_not_configured":
      return t.billing.errNotConfigured;
    case "billing_provider_error":
      return t.billing.errProvider;
    default:
      return apiErrorText(err, t, t.common.actionFailed);
  }
}

function statusWord(t: Dict, status: string): string {
  if (status === "past_due") return t.billing.statusPastDue;
  if (status === "canceled") return t.billing.statusCanceled;
  if (status === "suspended") return t.billing.statusSuspended;
  return t.billing.statusActive;
}

/** One limit, as a bar. A null limit is unlimited and draws empty. */
function UsageBar({ label, used, limit }: { label: string; used: number; limit: number | null }): JSX.Element {
  const { t } = useI18n();
  const fraction = usageFraction(used, limit);
  const full = limit !== null && used >= limit;
  return (
    <li>
      <span class="hbar-label">{label}</span>
      <span class="hbar-track">
        <span
          class="hbar"
          style={{ width: `${Math.round(fraction * 100)}%`, background: full ? "var(--danger)" : "var(--sea)" }}
        />
      </span>
      <span class="hbar-n">{t.billing.ofLimit(used, limit === null ? "∞" : String(limit))}</span>
    </li>
  );
}

export function BillingTab(): JSX.Element {
  const { t: all, lang } = useI18n();
  const t = all.billing;
  const { reload: reloadPlan } = usePlan();
  const [state, setState] = useState<BillingState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, show] = useStatus();
  const [busy, setBusy] = useState(false);
  // Read once: Stripe's return is in the hash, and the hash is cleaned below
  // so a reload does not announce a payment twice.
  const [checkout] = useState(() => checkoutResult(location.hash));

  const load = useCallback(async () => {
    try {
      const res = await api.billing();
      setState(res);
      setError(null);
    } catch (err) {
      setError(apiErrorText(err, all, all.billing.loadFailed));
    }
  }, [all.billing.loadFailed]);

  useEffect(() => {
    void load();
  }, [load]);

  // A success means the webhook is on its way; ask again in a moment so the
  // card is not stuck on the old plan, and drop the query either way.
  useEffect(() => {
    if (!checkout) return;
    // replaceState, not a hash assignment: assigning would add a history entry
    // (back would walk into the spent ?checkout= URL) and fire a hashchange
    // that re-renders the shell around this very banner.
    if (location.hash.includes("?")) history.replaceState(null, "", "#/settings/billing");
    if (checkout !== "success") return;
    const timer = window.setTimeout(() => {
      void load();
      reloadPlan();
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [checkout, load, reloadPlan]);

  if (error) {
    return (
      <div class="alert error" role="alert">
        {error}
        <div class="actions-row">
          <button class="btn sm" onClick={() => void load()}>
            {all.common.retry}
          </button>
        </div>
      </div>
    );
  }
  if (!state) return <div class="empty">{all.common.loading}</div>;

  const monthly = priceOf(state.prices, state.storedPlan);
  const planLabel = all.plan.names[state.plan] ?? state.plan;
  const storedLabel = all.plan.names[state.storedPlan] ?? state.storedPlan;

  async function run(fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      show("error", billingMessage(all, err));
    } finally {
      setBusy(false);
    }
  }

  const startCheckout = (plan: "starter" | "team"): void =>
    void run(async () => {
      const res = await api.checkout(plan);
      location.assign(res.url);
    });

  const openPortal = (): void =>
    void run(async () => {
      const res = await api.billingPortal();
      location.assign(res.url);
    });

  return (
    <>
      {checkout === "success" ? (
        <div class="alert" role="status">
          {t.checkoutSuccess}
        </div>
      ) : null}
      {checkout === "cancel" ? (
        <div class="alert warn" role="status">
          {t.checkoutCancel}
        </div>
      ) : null}
      <StatusLine status={status} />

      <Section title={t.planTitle} intro={t.intro}>
        <p class="plan-headline">
          <strong>{planLabel}</strong> <span class="chip">{statusWord(all, state.planStatus)}</span>
        </p>
        {state.inGrace && state.graceUntil ? (
          <div class="alert warn">{t.graceNote(storedLabel, longDate(state.graceUntil, lang))}</div>
        ) : null}
        {!state.inGrace && state.planStatus !== "suspended" && state.storedPlan !== state.plan ? (
          <div class="alert warn">{t.downgradedNote(storedLabel)}</div>
        ) : null}
        {state.planStatus === "suspended" ? <div class="alert warn">{t.suspendedNote}</div> : null}
        <p class="hint">
          {monthly === null ? "" : `${t.perMonth(money(monthly, lang))} · `}
          {state.currentPeriodEnd ? t.renews(longDate(state.currentPeriodEnd, lang)) : ""}
        </p>

        <h3 class="key-heading">{t.usageTitle}</h3>
        <ul class="hbars">
          <UsageBar label={t.sites} used={state.usage.sites} limit={state.limits.maxSites} />
          <UsageBar label={t.operators} used={state.usage.operators} limit={state.limits.maxOperators} />
        </ul>
      </Section>

      {!state.configured ? (
        <Section title={t.notConfiguredTitle}>
          <p class="intro">{t.notConfiguredBody}</p>
          <button class="btn" onClick={() => void load()}>
            {t.refresh}
          </button>
        </Section>
      ) : (
        <>
          {/* Nothing to upgrade to from the plan that has no limits. */}
          {state.hasSubscription || state.storedPlan === "unlimited" ? null : (
            <Section title={t.upgradeTitle} intro={t.upgradeIntro}>
              <div class="actions-row">
                {state.storedPlan === "starter" ? null : (
                  <button class="btn primary" disabled={busy} onClick={() => startCheckout("starter")}>
                    {t.upgradeStarter} · {t.perMonth(money(state.prices.starter, lang))}
                  </button>
                )}
                {state.storedPlan === "team" ? null : (
                  <button class="btn primary" disabled={busy} onClick={() => startCheckout("team")}>
                    {t.upgradeTeam} · {t.perMonth(money(state.prices.team, lang))}
                  </button>
                )}
              </div>
            </Section>
          )}

          <Section title={t.manageTitle} intro={t.manageHint}>
            <button class="btn" disabled={busy} onClick={openPortal}>
              {t.manage}
            </button>
          </Section>
        </>
      )}
    </>
  );
}
